import clsx from "clsx";
import React from "react";
import {
  DialogProps,
  Stack,
  Button,
  CircularProgress,
  Box,
  TextField,
  Divider,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import { BaseDialog } from "../../dialog/BaseDialog";
import { ThreadSkeleton } from "../../messages/ThreadSkeleton";

import ClientsService from "../../../services/ClientsService";
import useInfiniteScrollQuery from "../../../hooks/useInfiniteScroll";
import { useDebounce } from "../../../hooks/useDebounce";

import { ClientList } from "./ClientList";
import {
  ClientInfoDto,
  UserInviteStatus,
} from "@growth-machine-llc/stridist-api-client";
import { COACH_CLIENTS_LIST_QUERY_KEY } from "../../coach-clients/CoachClientsListScreen";

const useStyles = makeStyles((theme) => ({
  root: {},
  paper: {
    width: 524,
    maxHeight: "90vh",
  },
  list: {
    height: 350,
    overflowY: "auto",
  },
  button: {
    height: theme.spacing(7),
    fontSize: 16,
    fontWeight: "bold",
    lineHeight: "20px",
    marginTop: theme.spacing(2),
  },
  loaderWrapper: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2, 0),
  },
}));

const PAGE_SIZE = 10;

export interface ClientDialogProps extends Omit<DialogProps, "onSelect"> {
  onSelect?: (event: React.MouseEvent, client: ClientInfoDto) => void;
  statuses?: UserInviteStatus[];
}

export function ClientDialog(props: ClientDialogProps) {
  const s = useStyles();
  const {
    onSelect,
    className,
    statuses = [UserInviteStatus.ACTIVE],
    ...other
  } = props;

  const [filter, setFilter] = React.useState("");
  const debouncedFilter = useDebounce(filter, 300);
  const [selectedClient, setSelectedClient] =
    React.useState<ClientInfoDto | null>(null);

  const handleFilterChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFilter(event.target.value);
    },
    [],
  );

  const handleSelect = React.useCallback(
    (_event: React.MouseEvent, client: ClientInfoDto) => {
      const alreadySelected = selectedClient?.id === client.id;

      if (alreadySelected) {
        setSelectedClient(null);
        return;
      }

      setSelectedClient(client);
    },

    [setSelectedClient, selectedClient],
  );

  const {
    data: clientPages,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    ref: infiniteScrollRef,
  } = useInfiniteScrollQuery({
    queryKey: [COACH_CLIENTS_LIST_QUERY_KEY, debouncedFilter, statuses],
    queryFn: ({ pageParam = 1 }) =>
      ClientsService.getClientsFiltered(
        pageParam as number,
        PAGE_SIZE,
        statuses,
        undefined,
        debouncedFilter,
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) =>
      lastPage?.hasNextPage ? pages.length + 1 : undefined,
  });

  const clients: ClientInfoDto[] =
    clientPages?.pages.flatMap((page) => page.items) ?? [];

  React.useEffect(() => {
    const selectedInClients = clients.find((c) => c.id === selectedClient?.id);
    if (!selectedInClients) {
      setSelectedClient(null);
    }
  }, [clients, selectedClient]);

  return (
    <BaseDialog
      className={clsx(s.root, className)}
      classes={{ paper: s.paper }}
      title="Profile Preview"
      {...other}
    >
      <TextField
        variant="outlined"
        fullWidth
        value={filter}
        onChange={handleFilterChange}
        label="Search clients"
      />

      <Box className={s.list}>
        {isLoading ? (
          <Stack spacing={2} sx={{ p: 1, mt: 1 }}>
            {Array.from({ length: 3 }).map((_, i) => (
              <ThreadSkeleton key={i} />
            ))}
          </Stack>
        ) : (
          <>
            <ClientList
              clients={clients}
              hasNext={!!hasNextPage}
              loading={isFetchingNextPage}
              onLoadMore={fetchNextPage}
              onSelect={handleSelect}
              selectedClient={selectedClient}
              singleSelect
              debounceQuery={debouncedFilter}
            />
            {hasNextPage && (
              <div ref={infiniteScrollRef} style={{ height: 10 }} />
            )}

            {isFetchingNextPage && (
              <div className={s.loaderWrapper}>
                <CircularProgress size={24} />
              </div>
            )}
          </>
        )}
      </Box>

      <Divider />
      <Button
        className={s.button}
        fullWidth
        variant="contained"
        children="Preview"
        onClick={(e) => selectedClient && onSelect?.(e as any, selectedClient)}
        disabled={!selectedClient}
      />
    </BaseDialog>
  );
}
